"use client";

import React from "react";
import * as Sentry from "@sentry/nextjs";

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  context?: string;
}

interface State {
  hasError: boolean;
  errorId?: string;
}

export default class AppErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    const errorId = Sentry.captureException(error, {
      contexts: {
        react: { componentStack: info.componentStack },
        app: { context: this.props.context ?? "unknown" },
      },
    });
    this.setState({ errorId: String(errorId) });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex flex-col items-center justify-center p-8 text-center gap-3">
          <div className="text-3xl">⚠️</div>
          <p className="font-medium text-gray-700">Something went wrong</p>
          <p className="text-sm text-gray-400">This section failed to load. The issue has been reported automatically.</p>
          {this.state.errorId ? <p className="text-xs text-gray-300 font-mono">Error ID: {this.state.errorId}</p> : null}
          <button onClick={() => this.setState({ hasError: false, errorId: undefined })} className="text-sm text-blue-600 hover:underline mt-2">
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
